// Realtime A3 instrumentation — circular buffer of (source, topic, edgeTs, recvTs)
// for use during the EMQX broker-migration A3 mirror run. Exposed at
// `window.__realtimePerf` so an operator can inspect from DevTools.
//
// Recorded latency = recvTsMs - edgeTsMs. This includes any clock skew between
// the edge node that stamped `ts` and the browser. Within a single tab the
// skew is constant, so p50/p90/p99/max move correctly; cross-tab comparison
// (MQTT-tab vs WS-tab) cancels the skew by subtracting matched edgeTs entries.
//
// Lifetime: ring of MAX_ENTRIES; dropped silently when full. Also calls
// `performance.mark()` so Chrome DevTools' Performance panel timeline includes
// per-receive markers when the user records a profile.

export type RealtimeSource = "mqtt" | "ws";
export type RealtimeMode = "samples" | "changes";

interface Entry {
  source: RealtimeSource;
  topic: string;
  mode: RealtimeMode;
  edgeTsMs: number;
  recvTsMs: number;
}

interface SummaryRow {
  source: RealtimeSource;
  topic: string;
  mode: RealtimeMode;
  count: number;
  p50_ms: number | null;
  p90_ms: number | null;
  p99_ms: number | null;
  min_ms: number | null;
  max_ms: number | null;
}

const MAX_ENTRIES = 2000;

const ring: (Entry | undefined)[] = new Array(MAX_ENTRIES);
let writeIdx = 0;
let total = 0;

export function record(source: RealtimeSource, topic: string, edgeTsMs: number): void {
  if (typeof edgeTsMs !== "number" || !Number.isFinite(edgeTsMs)) return;
  const recvTsMs = Date.now();
  ring[writeIdx] = { source, topic, mode: "samples", edgeTsMs, recvTsMs };
  writeIdx = (writeIdx + 1) % MAX_ENTRIES;
  total += 1;
  try {
    performance.mark(`realtime:${source}:${topic}`);
  } catch {
    // performance.mark can throw on some Safari versions
  }
}

// Tracked-value previous-state per (key) — populated by recordChange.
// `key` is a stable string identifier like "sys-123:p" or "sys-123:i_peak".
// Each entry remembers the last value we saw for that key so the next call
// can decide if it's a change worth recording. Numbers are compared with
// epsilon to avoid recording floating-point noise as a change.
const _prevValueByKey = new Map<string, number>();
const _CHANGE_EPSILON = 1e-9;

/**
 * Record a value-change event. Filters out non-changes (same-as-prev value)
 * silently. Use this in hook message handlers AFTER the new value is computed
 * for a tracked field — it gates by previous value, so only actual changes
 * land in the ring.
 *
 * Latency for change events = recvWall - edgeTsMs of the sample that
 * carried the new value. This is the metric that answers "how long from
 * value-changed-on-edge to value-visible-on-this-path" (within ~1 frame
 * of React commit + paint, which is small relative to the network path).
 *
 * Cross-tab: open both ?mqtt_v2=1 and default tabs, summary() the changes
 * section in each, subtract path p50s to get the operator-felt
 * MQTT-vs-WS delta. Same edge ts for the underlying change so clock skew
 * cancels at the change-event level.
 */
export function recordChange(
  source: RealtimeSource,
  topic: string,
  key: string,
  newValue: number | null | undefined,
  edgeTsMs: number,
): void {
  if (typeof edgeTsMs !== "number" || !Number.isFinite(edgeTsMs)) return;
  if (newValue == null || !Number.isFinite(newValue)) return;
  const prev = _prevValueByKey.get(key);
  if (prev !== undefined && Math.abs(prev - newValue) < _CHANGE_EPSILON) {
    return;
  }
  _prevValueByKey.set(key, newValue);
  if (prev === undefined) {
    // First value seen for this key — don't record (no change happened,
    // just initialization). Otherwise every first-sample after page load
    // would register as a "change" and skew the histogram.
    return;
  }
  const recvTsMs = Date.now();
  ring[writeIdx] = { source, topic, mode: "changes", edgeTsMs, recvTsMs };
  writeIdx = (writeIdx + 1) % MAX_ENTRIES;
  total += 1;
}

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
  return sorted[idx];
}

export function summary(): SummaryRow[] {
  // Group by (source, topic, mode). The mode dimension separates per-sample
  // receive latency from per-value-change presentation latency — the latter
  // is what answers "how long from data-change to dashboard-shows-change".
  // The clock reference of `edge_ts` also varies per topic (WS dyno_samples
  // forwards the edge ts; WS dyno_snapshot uses cloud-api's ts), so the
  // topic dimension also matters.
  const byKey = new Map<string, number[]>();
  const filled = Math.min(total, MAX_ENTRIES);
  for (let i = 0; i < filled; i += 1) {
    const e = ring[i];
    if (!e) continue;
    const latency = e.recvTsMs - e.edgeTsMs;
    const key = `${e.source}::${e.topic}::${e.mode}`;
    let arr = byKey.get(key);
    if (!arr) {
      arr = [];
      byKey.set(key, arr);
    }
    arr.push(latency);
  }
  const rows: SummaryRow[] = [];
  for (const [key, samples] of byKey) {
    const [source, topic, mode] = key.split("::");
    samples.sort((a, b) => a - b);
    rows.push({
      source: source as RealtimeSource,
      topic,
      mode: mode as RealtimeMode,
      count: samples.length,
      p50_ms: percentile(samples, 0.5),
      p90_ms: percentile(samples, 0.9),
      p99_ms: percentile(samples, 0.99),
      min_ms: samples[0] ?? null,
      max_ms: samples[samples.length - 1] ?? null,
    });
  }
  // Sort changes-mode rows first within each (source, topic) — those are the
  // operator-relevant latency numbers; samples-mode is supplementary.
  rows.sort((a, b) => {
    const modeOrder = (m: RealtimeMode) => (m === "changes" ? 0 : 1);
    if (a.source !== b.source) return a.source.localeCompare(b.source);
    if (a.topic !== b.topic) return a.topic.localeCompare(b.topic);
    return modeOrder(a.mode) - modeOrder(b.mode);
  });
  // eslint-disable-next-line no-console
  console.table(rows);
  return rows;
}

export function reset(): void {
  for (let i = 0; i < MAX_ENTRIES; i += 1) ring[i] = undefined;
  writeIdx = 0;
  total = 0;
}

export function dump(): string {
  const rows: string[] = ["source,topic,edge_ts_ms,recv_ts_ms,latency_ms"];
  const filled = Math.min(total, MAX_ENTRIES);
  const start = total < MAX_ENTRIES ? 0 : writeIdx;
  for (let i = 0; i < filled; i += 1) {
    const e = ring[(start + i) % MAX_ENTRIES];
    if (!e) continue;
    rows.push(`${e.source},${e.topic},${e.edgeTsMs},${e.recvTsMs},${e.recvTsMs - e.edgeTsMs}`);
  }
  return rows.join("\n");
}

if (typeof window !== "undefined") {
  (window as unknown as { __realtimePerf: object }).__realtimePerf = {
    record,
    recordChange,
    summary,
    reset,
    dump,
  };
}
